import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { backendUrl } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import AppBanner from './AppBanner';

const CreatePasswordPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');

  // Real logout handler (same as onboarding)
  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      alert(t('logoutError') + ': ' + error.message);
    } else {
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    const init = async () => {
      const emailParam = searchParams.get('email');
      if (emailParam) {
        setEmail(emailParam);
        return;
      }
      // If no email param, allow logged-in Google users to set a password directly
      try {
        const { data } = await supabase.auth.getUser();
        const currentEmail = data?.user?.email;
        if (currentEmail) {
          setEmail(currentEmail);
          return;
        }
      } catch (_) {}
      setError(t('invalidPasswordCreationLink'));
      setTimeout(() => navigate('/login'), 3000);
    };
    init();
  }, [searchParams, navigate, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password.length < 8) {
      setError(t('passwordMinLength'));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    try {
      // If user is already logged in (Google), set password directly via Supabase
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session) {
        const { error: updateErr } = await supabase.auth.updateUser({ password });
        if (updateErr) throw updateErr;
        // Mark metadata and proceed
        await supabase.auth.updateUser({ data: { has_password: true } });
        navigate('/onboarding');
        return;
      }

      // Fallback: backend sets password by email (legacy path)
      // Note: This path requires the user to be authenticated (logged in via Google OAuth)
      // If not authenticated, the user should use the Supabase direct method above
      const accessToken = sessionData?.session?.access_token;
      
      if (!accessToken) {
        setError(t('invalidPasswordCreationLink') || 'Please log in first to set your password');
        setLoading(false);
        return;
      }

      const setPasswordUrl = `${backendUrl}/auth/set-password`;
      const response = await fetch(setPasswordUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, accessToken })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || t('failedToSetPassword'));
        setLoading(false);
        return;
      }
      // Sign in with the new password
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        setError(t('passwordSetButSignInFailed'));
        setLoading(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }
      navigate('/onboarding');
    } catch (error) {
      setError(t('failedToSetPassword'));
      setLoading(false);
    }
  };

  if (!email) {
    return (
      <div style={{ 
        maxWidth: '420px', 
        margin: '96px auto',
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        padding: '2.5rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{ color: '#e57373', marginBottom: '16px' }}>
          {t('loading')}
        </div>
      </div>
    );
  }

  return (
    <>
      <AppBanner onLogout={handleLogout} />
      <div style={{ 
      maxWidth: '420px', 
      margin: '96px auto',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
      padding: '2.5rem 2rem',
      textAlign: 'center'
    }}>
      <h2 style={{ 
        marginBottom: '24px',
        color: '#2c6e49',
        fontWeight: '600'
      }}>{t('createYourPassword')}</h2>
      
      <p style={{ 
        color: '#666', 
        marginBottom: '24px',
        fontSize: '0.95rem'
      }}>
        {t('welcomeCreatePassword')}
      </p>
      
      {error && <div style={{ color: '#e57373', marginBottom: '16px' }}>{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <input
          type="password"
          placeholder={t('createPassword')}
          value={password}
          onChange={e => setPassword(e.target.value)}
          style={{ 
            width: '100%', 
            padding: '0.8rem', 
            marginBottom: '12px', 
            borderRadius: '8px', 
            border: '1px solid #ccc', 
            fontSize: '1rem',
            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
            boxSizing: 'border-box'
          }}
          autoFocus
        />
        <input
          type="password"
          placeholder={t('confirmPassword')}
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          style={{ 
            width: '100%', 
            padding: '0.8rem', 
            marginBottom: '20px', 
            borderRadius: '8px', 
            border: '1px solid #ccc', 
            fontSize: '1rem',
            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
            boxSizing: 'border-box'
          }}
        />
        <button
          type="submit"
          className="btn btn-primary"
          style={{ 
            width: '100%', 
            padding: '0.9rem', 
            fontWeight: '600', 
            fontSize: '1.1rem', 
            borderRadius: '8px', 
            border: 'none', 
            background: 'linear-gradient(135deg, #a8e063 0%, #56ab2f 100%)',
            color: '#fff', 
            cursor: loading ? 'not-allowed' : 'pointer', 
            opacity: loading ? 0.7 : 1,
            boxShadow: '0 2px 8px rgba(86, 171, 47, 0.2)',
            transition: 'all 0.2s ease-in-out'
          }}
          disabled={loading}
        >
          {loading ? t('creatingAccount') : t('completeSetup')}
        </button>
      </form>
      
      <div style={{ 
        marginTop: '16px', 
        color: '#888',
        fontSize: '0.9rem'
      }}>
        <p>{t('yourEmail')}: <strong>{email}</strong></p>
      </div>
      </div>
    </>
  );
};

export default CreatePasswordPage; 